import { inject, Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of, switchMap, concatMap, map, catchError, from, defer, combineLatest, filter } from 'rxjs';
import { OrganizationsService } from "projects/datexapplication/src/app/common/services/application.service";
import * as OrganizationsActions from "./organizations.actions";
import { MsalService } from "@azure/msal-angular";
import { PublicClientApplication } from "@azure/msal-browser";
import { AzureResourceManagerService } from "../../../services/azure-resource-manager.service";
import { DATEX_TENANTID } from "../../../../common/constants";
import { Store } from "@ngrx/store";
import { AppState } from "../app.state";
import { selectMyOrganizationLoaded, selectOrganizationsLoaded } from "./organizations.selectors";

@Injectable()
export class OrganizationsEffects {

  private actions$ = inject(Actions);
  private store = inject(Store<AppState>);
  private organizationsService = inject(OrganizationsService);
  private authService = inject(MsalService);
  private armService = inject(AzureResourceManagerService);

  // Only load if not already loaded - prevents duplicate API calls
  loadMyOrganization$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(OrganizationsActions.loadMyOrganization)),
      this.store.select(selectMyOrganizationLoaded)
    ]).pipe(
      filter(([_, loaded]) => !loaded),
      switchMap(() =>
        this.organizationsService.getMine().pipe(
          map(organization => OrganizationsActions.loadMyOrganizationSuccess({ organization })),
          catchError(error => of(OrganizationsActions.loadMyOrganizationFailure({ title: 'Error loading organization details', error: error.message })))
        )
      )
    )
  );

  loadMyOrganizationSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.loadMyOrganizationSuccess),
      map(({ organization }) => OrganizationsActions.selectOrganization({ organization }))
    )
  );

  // Only load if not already loaded or forceReload is true - prevents duplicate API calls
  // Use concatMap to ensure sequential execution - prevents cancellation of in-flight operations
  loadOrganizations$ = createEffect(() =>
    combineLatest([
      this.actions$.pipe(ofType(OrganizationsActions.loadOrganizations)),
      this.store.select(selectOrganizationsLoaded)
    ]).pipe(
      filter(([{ forceReload }, loaded]) => forceReload || !loaded),
      concatMap(() => {
        return this.armService.getTenants().pipe(
          map(tenants => tenants.map(t => t.id)),
          concatMap(tenantIds => {
            const activeAccount = this.authService.instance.getActiveAccount();
            const isDatexUser = activeAccount?.tenantId === DATEX_TENANTID;

            if (isDatexUser) {
              // For Datex users: load all organizations using existing getAll endpoint
              // Map Organization to OrganizationDto (same fields)
              return this.organizationsService.getAll().pipe(
                map(orgs => OrganizationsActions.loadOrganizationsSuccess({
                  organizations: orgs.map(o => ({
                    id: o.id,
                    name: o.name,
                    referenceName: o.referenceName,
                    description: o.description,
                    tenantId: o.tenantId,
                    homeTenantId: o.homeTenantId,
                    externalEntraIdDomainName: o.externalEntraIdDomainName,
                    externalEntraId: o.externalEntraId,
                    devOpsOrganization: o.devOpsOrganization
                  })),
                  memberTenantIds: tenantIds
                }))
              );
            } else {
              // For non-Datex users: load only orgs they have access to
              return this.organizationsService.getByTenants(tenantIds).pipe(
                map(orgs => OrganizationsActions.loadOrganizationsSuccess({
                  organizations: orgs,
                  memberTenantIds: tenantIds
                }))
              );
            }
          }),
          catchError(error => of(OrganizationsActions.loadOrganizationsFailure({ title: 'Failed to load organizations', error: error.message })))
        );
      })
    )
  );

  // Use concatMap to ensure sequential execution - prevents cancellation which could leave MSAL in bad state
  // Use defer for lazy evaluation - ensures MSAL calls happen at subscription time
  selectOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.selectOrganization),
      concatMap(({ organization }) => defer(() => {
        const allAccounts = this.authService.instance.getAllAccounts();
        const accountFromTenant = allAccounts.find(a => a.tenantId === organization.tenantId);

        // account is already logged in
        if (accountFromTenant) {
          this.authService.instance.setActiveAccount(accountFromTenant);
          return of(OrganizationsActions.selectOrganizationSuccess({ organization }));
        }

        // https://github.com/AzureAD/microsoft-authentication-library-for-js/blob/dev/lib/msal-angular/docs/multi-tenant.md

        // need to create a new client application which point to a specific tenant
        const config = this.authService.instance.getConfiguration();
        config.auth.authority = `https://login.microsoftonline.com/${organization.tenantId}`;
        const publicClientApp = new PublicClientApplication(config);

        // CRITICAL: Initialize the new instance BEFORE assigning to authService
        // This prevents other code from accessing an uninitialized instance
        return from(publicClientApp.initialize()).pipe(
          switchMap(() => {
            // Only assign AFTER initialization is complete
            this.authService.instance = publicClientApp;
            return from(publicClientApp.loginPopup()).pipe(
              map(result => {
                publicClientApp.setActiveAccount(result.account);
                return OrganizationsActions.selectOrganizationSuccess({ organization });
              })
            );
          }),
          catchError(error => of(OrganizationsActions.selectOrganizationFailure({ title: 'Error login to tenant', error: error.message })))
        );
      }))
    )
  );

  createOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.createOrganization),
      switchMap(({ organization }) =>
        this.organizationsService.createOrganizationWithUser(organization).pipe(
          map((result) => OrganizationsActions.createOrganizationSuccess({ message: `Organization ${organization.name} successfully created`, organization: result })),
          catchError((error) => of(OrganizationsActions.createOrganizationFailure({ title: `Failed to create organization ${organization.name}`, error: error.message })))
        )
      )
    )
  );

  updateOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.updateOrganization),
      switchMap(({ organization }) =>
        this.organizationsService.update(organization).pipe(
          map((result) => OrganizationsActions.updateOrganizationSuccess({ message: `Organization ${organization.name} successfully updated`, organization: result })),
          catchError((error) => of(OrganizationsActions.updateOrganizationFailure({ title: `Failed to update organization ${organization.name}`, error: error.message })))
        )
      )
    )
  );

  deleteOrganization$ = createEffect(() =>
    this.actions$.pipe(
      ofType(OrganizationsActions.deleteOrganization),
      switchMap(({ organization }) =>
        this.organizationsService.delete(organization.id).pipe(
          map((result) => OrganizationsActions.deleteOrganizationSuccess({ message: `Organization ${organization.name} successfully deleted`, organization })),
          catchError((error) => of(OrganizationsActions.deleteOrganizationFailure({ title: `Failed to delete organization ${organization.name}`, error: error.message })))
        )
      )
    )
  );
}